// src/modules/app-views/crm/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Collapse, Row, Tag, Tooltip } from 'antd';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import crmService from '../../service';
import BoardsGroup from './components/BoardsGroup';
import BoardsGroupTitle from './components/BoardsGroupTitle';
import BoardSettingsModal from './modals/BoardSettingsModal';
import BackToHomeLink from 'components/core/BackToHomeLink';
import translateX from 'utils/translateX';
import { RightOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { clone } from 'utils/helpers';
import { getStorageModuleItem, setStorageModuleItem } from 'stores/module';
import DraggableCollapseGroup from './components/DraggableCollapseGroup';
import { useUserRole } from '../../useUserRole';

// Re-export role constants so other modules can import from here if needed
export { test, USER_ROLE } from '../../roleConfig';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CrmBoards = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Subscribe to parameters so component re-renders when SSE delivers them after F5
  const storedParameters = useZustand('app', 'parameters');
  const storedSubscription = useZustand('auth', 'subscription');
  const { canManage } = useUserRole();
  const setReadyStore = useZustand('module', 'setReady');
  const setPageStore = useZustand('module', 'setPage');
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const boardGroups = storedParameters?.modules?.crm?.boardGroups || [];
  const boards = (storedParameters?.modules?.crm?.boards || []).filter(b => !b.removedAt && !b.archivedAt);
  // Boards whose boardGroupUid is missing or doesn't match any known group
  const knownGroupUids = new Set(boardGroups.map(g => g.uid || g._id));
  const orphanedBoards = boards.filter(b => !b.boardGroupUid || !knownGroupUids.has(b.boardGroupUid));
  // Per-user ordering override saved with private: true (each user's own preference)
  const userBoardOrders = storedParameters?.modules?.crm?.boardOrders;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [activeKeys, setActiveKeys] = useState([]);
  const [activeDragBoard, setActiveDragBoard] = useState(null);
  const [activeDragGroup, setActiveDragGroup] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // When subscription changes, reset ready state so SimpleLoadingPage shows
  // while SSE reconnects and delivers new parameters for the new subscription
  useEffect(() => {
    setReadyStore(false);
    setActiveKeys([]);
  }, [storedSubscription?._id]);

  // Dismiss SimpleLoadingPage once parameters are available (no DocumentProvider to handle this)
  useEffect(() => {
    if (storedParameters?.modules?.crm) setReadyStore(true);
  }, [storedParameters?.modules?.crm]);

  // Clear kanban scroll position cache when entering the boards page
  useEffect(() => {
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('crm_kanban_scroll_'))
      .forEach(k => sessionStorage.removeItem(k));
  }, []);

  useEffect(() => {
    // Load collapsed state from localStorage (default: all closed = empty array)
    if (boardGroups?.length && activeKeys.length === 0) {
      const storedKeys = getStorageModuleItem('boardsCollapseActiveKeys') || [];
      setActiveKeys(storedKeys);
    }
  }, [boardGroups]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOpenBoard = useCallback(
    board => {
      const boardUid = board.uid;
      setPageStore({
        navigate,
        pageName: 'kanban',
        params: { boardUid },
      });
    },
    [navigate, setPageStore]
  );

  const handleEditBoard = useCallback((e, board, group) => {
    e.stopPropagation();
    setSelectedGroup(group);
    setSelectedBoard(board);
    setShowBoardModal(true);
  }, []);

  const handleDeleteGroup = async groupUid => {
    const groupBoards = boards.filter(b => b.boardGroupUid === groupUid);

    if (groupBoards.length) {
      appService.message({ flag: 'w', text: 'cannot_remove_group_with_item' });
      return;
    }

    appService.modal({
      flag: 'confirm',
      title: 'remove_group',
      content: 'delete_group_description',
      okText: 'yes',
      cancelText: 'no',
      okButtonProps: { danger: true },
      onOk: async () => {
        await crmService.removeBoardGroup(groupUid);
      },
    });
  };

  const handleUpdateGroupTitle = async (groupUid, title) => {
    await crmService.updateBoardGroupTitle(groupUid, title);
    setEditingKey(null);
  };

  const handleCollapseChange = keys => {
    setActiveKeys(keys);
    setStorageModuleItem('boardsCollapseActiveKeys', keys);
  };

  const handleDragStart = useCallback(
    event => {
      const activeType = event.active.data?.current?.type;
      if (activeType === 'group') {
        const group = boardGroups.find(g => (g.uid || g._id) === event.active.id);
        setActiveDragGroup(group || null);
      } else {
        const board = boards.find(b => (b.uid || b._id) === event.active.id);
        setActiveDragBoard(board || null);
      }
    },
    [boards, boardGroups]
  );

  const handleDragEnd = useCallback(
    async event => {
      const { active, over } = event;

      setActiveDragBoard(null);
      setActiveDragGroup(null);

      if (!over) return;

      const activeId = active.id;
      const overId = over.id;

      if (activeId === overId) return;

      const activeType = active.data?.current?.type;

      // ── Group reorder ────────────────────────────────────────────────────────
      if (activeType === 'group') {
        // Guests cannot reorder groups
        if (!canManage) return;

        const orderedG = applyCustomOrder(boardGroups, userBoardOrders?.groupOrder);
        const oldIndex = orderedG.findIndex(g => (g.uid || g._id) === activeId);
        const newIndex = orderedG.findIndex(g => (g.uid || g._id) === overId);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = arrayMove(orderedG, oldIndex, newIndex);
        const newGroupOrder = reordered.map(g => g.uid || g._id);

        try {
          // Canonical: updates order for everyone's first-time view
          await crmService.reorderBoardGroups(reordered);
          // Personal: admin's own view matches the canonical they just set
          appService.updateModulePreferences({
            moduleId: 'crm',
            category: 'boardOrders',
            data: { groupOrder: newGroupOrder, boardOrder: userBoardOrders?.boardOrder || {} },
            private: true,
          });
        } catch (err) {
          console.error('[CrmBoards] Error reordering groups:', err);
          appService.message({ flag: 'e', text: 'error_reordering_groups' });
        }
        return;
      }

      // ── Board reorder ────────────────────────────────────────────────────────
      const activeData = active.data?.current;
      const overData = over.data?.current;
      const sourceGroupUid = activeData?.groupUid;
      const destGroupUid = overData?.groupUid || over.id;

      // Guests cannot move boards between groups
      if (!canManage && sourceGroupUid !== destGroupUid) return;

      const movedBoard = boards.find(b => (b.uid || b._id) === activeId);
      if (!movedBoard) return;

      if (sourceGroupUid === destGroupUid) {
        // ── Within same group: save personal order (all users) + canonical (admin) ──
        const rawGroupBoards = boards.filter(b => b.boardGroupUid === sourceGroupUid);
        const orderedB = applyCustomOrder(rawGroupBoards, userBoardOrders?.boardOrder?.[sourceGroupUid]);
        const orderedIds = orderedB.map(b => b.uid || b._id);
        const oldIndex = orderedIds.indexOf(activeId);
        const newIndex = orderedIds.indexOf(overId);
        if (oldIndex === -1 || newIndex === -1) return;

        const reorderedBoards = arrayMove(orderedB, oldIndex, newIndex);
        const newPersonalOrder = reorderedBoards.map(b => b.uid || b._id);

        // Personal order for all users (optimistic via updateModulePreferences)
        appService.updateModulePreferences({
          moduleId: 'crm',
          category: 'boardOrders',
          data: {
            groupOrder: userBoardOrders?.groupOrder || [],
            boardOrder: { ...(userBoardOrders?.boardOrder || {}), [sourceGroupUid]: newPersonalOrder },
          },
          private: true,
        });

        // Canonical order for admin
        if (canManage) {
          try {
            const newBoards = clone(boards);
            reorderedBoards.forEach((board, idx) => {
              const i = newBoards.findIndex(b => (b.uid || b._id) === (board.uid || board._id));
              if (i >= 0) newBoards[i].order = idx;
            });
            await crmService.reorderBoards(newBoards);
          } catch (err) {
            console.error('[CrmBoards] Error reordering boards:', err);
            appService.message({ flag: 'e', text: 'error_reordering_boards' });
          }
        }
      } else {
        // ── Cross-group (admin only): save canonical + update personal order ──
        const newBoards = clone(boards);
        const boardIdx = newBoards.findIndex(b => (b.uid || b._id) === activeId);
        if (boardIdx >= 0) newBoards[boardIdx].boardGroupUid = destGroupUid;

        const destGroupBoards = newBoards
          .filter(b => b.boardGroupUid === destGroupUid)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        const overIdx = destGroupBoards.findIndex(b => (b.uid || b._id) === overId);
        const insertIdx = overIdx >= 0 ? overIdx : destGroupBoards.length;

        destGroupBoards.forEach((board, idx) => {
          const i = newBoards.findIndex(b => (b.uid || b._id) === (board.uid || board._id));
          if (i >= 0) newBoards[i].order = idx >= insertIdx ? idx + 1 : idx;
        });
        const movedIdx = newBoards.findIndex(b => (b.uid || b._id) === activeId);
        if (movedIdx >= 0) newBoards[movedIdx].order = insertIdx;

        // Update personal order: remove from source, insert at drop position in dest
        const newBoardOrder = { ...(userBoardOrders?.boardOrder || {}) };
        if (newBoardOrder[sourceGroupUid]) {
          newBoardOrder[sourceGroupUid] = newBoardOrder[sourceGroupUid].filter(id => id !== activeId);
        }
        const destOrdered = applyCustomOrder(
          boards.filter(b => b.boardGroupUid === destGroupUid),
          newBoardOrder[destGroupUid]
        ).map(b => b.uid || b._id);
        const overPos = destOrdered.indexOf(overId);
        if (overPos >= 0) destOrdered.splice(overPos, 0, activeId);
        else destOrdered.push(activeId);
        newBoardOrder[destGroupUid] = destOrdered;

        appService.updateModulePreferences({
          moduleId: 'crm',
          category: 'boardOrders',
          data: { groupOrder: userBoardOrders?.groupOrder || [], boardOrder: newBoardOrder },
          private: true,
        });

        try {
          await crmService.reorderBoards(newBoards);
        } catch (err) {
          console.error('[CrmBoards] Error reordering boards:', err);
          appService.message({ flag: 'e', text: 'error_reordering_boards' });
        }
      }
    },
    [boards, boardGroups, userBoardOrders, canManage]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Apply per-user ordering: falls back to canonical order when no personal preference exists
  const orderedGroups = applyCustomOrder(boardGroups, userBoardOrders?.groupOrder);
  const groupIds = orderedGroups.map(g => g.uid || g._id);

  const renderedGroups = orderedGroups.map(group => {
    const groupUid = group.uid || group._id;
    const rawGroupBoards = boards.filter(b => b.boardGroupUid === groupUid);
    const groupBoards = applyCustomOrder(rawGroupBoards, userBoardOrders?.boardOrder?.[groupUid]);

    return (
      <DraggableCollapseGroup key={groupUid} groupUid={groupUid}>
        <Collapse
          size="large"
          className="w-100"
          activeKey={activeKeys.includes(groupUid) ? [groupUid] : []}
          onChange={() => {
            if (activeKeys.includes(groupUid)) {
              handleCollapseChange(activeKeys.filter(k => k !== groupUid));
            } else {
              handleCollapseChange([...activeKeys, groupUid]);
            }
          }}
          items={[
            {
              key: groupUid,
              label: (
                <BoardsGroupTitle
                  group={group}
                  boardCount={groupBoards.length}
                  editingKey={editingKey}
                  setEditingKey={setEditingKey}
                  onDelete={handleDeleteGroup}
                  onUpdateTitle={handleUpdateGroupTitle}
                  canManage={canManage}
                />
              ),
              children: (
                <BoardsGroup
                  boards={groupBoards}
                  group={group}
                  setSelectedGroup={setSelectedGroup}
                  setSelectedBoard={setSelectedBoard}
                  setShowBoardModal={setShowBoardModal}
                  onOpenBoard={handleOpenBoard}
                  onEditBoard={handleEditBoard}
                  canManage={canManage}
                />
              ),
            },
          ]}
        />
      </DraggableCollapseGroup>
    );
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="my-4 container-fluid">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Row gutter={24}>
          {groupIds.length > 0 && (
            <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
              <div className="w-100">{renderedGroups}</div>
            </SortableContext>
          )}
          {orphanedBoards.length > 0 && (
            <div className="w-100 mt-3">
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 16,
                  padding: '8px 4px',
                }}
              >
                {orphanedBoards.map(board => {
                  const columnCount = board.columns?.length || 0;
                  return (
                    <Card
                      key={board.uid || board._id}
                      hoverable
                      size="small"
                      style={{ borderTop: `3px solid ${board.backColor || '#1890ff'}`, marginBottom: 0 }}
                      title={board.title}
                      extra={
                        canManage ? (
                          <Tooltip title={translateX('settings')}>
                            <Button
                              type="text"
                              size="small"
                              icon={<SettingOutlined />}
                              onClick={e => handleEditBoard(e, board, null)}
                              onPointerDown={e => e.stopPropagation()}
                              style={{ minWidth: 40, minHeight: 40 }}
                            />
                          </Tooltip>
                        ) : undefined
                      }
                      onClick={() => handleOpenBoard(board)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                        {board.description ? (
                          <p className="mb-0 text-muted" style={{ fontSize: 12 }}>{board.description}</p>
                        ) : (
                          <div>&nbsp;</div>
                        )}
                        <Tag className="mt-3" color="blue" style={{ alignSelf: 'flex-start' }}>
                          {columnCount} {translateX(columnCount === 1 ? 'column' : 'columns')}
                        </Tag>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </Row>

        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
          {activeDragGroup ? (
            <div
              style={{
                background: '#fafafa',
                borderRadius: 8,
                border: '1px solid #d9d9d9',
                padding: '16px 24px',
                cursor: 'grabbing',
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div className="d-flex align-items-center">
                <RightOutlined
                  style={{ fontSize: 12, marginRight: 12, color: 'rgba(0,0,0,0.45)' }}
                />
                <span className="font-weight-semibold">{activeDragGroup.label}</span>
                <Badge
                  count={
                    boards.filter(
                      b => b.boardGroupUid === (activeDragGroup.uid || activeDragGroup._id)
                    ).length
                  }
                  showZero
                  style={{ marginLeft: 8, backgroundColor: '#999' }}
                />
              </div>
            </div>
          ) : activeDragBoard ? (
            <Card
              size="small"
              style={{
                borderTop: `3px solid ${activeDragBoard.backColor || '#1890ff'}`,
                minHeight: 120,
                cursor: 'grabbing',
                boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                marginBottom: 0,
              }}
              title={activeDragBoard.title}
            >
              <div>
                <Tag>
                  {activeDragBoard.columns?.length || 0}{' '}
                  {translateX((activeDragBoard.columns?.length || 0) === 1 ? 'column' : 'columns')}
                </Tag>
                {activeDragBoard.description && (
                  <p className="mt-2 mb-0 text-muted" style={{ fontSize: 12 }}>
                    {activeDragBoard.description}
                  </p>
                )}
              </div>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>

      <BoardSettingsModal
        show={showBoardModal}
        setShow={setShowBoardModal}
        group={selectedGroup}
        board={selectedBoard}
        setBoard={setSelectedBoard}
      />

      <BackToHomeLink />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CrmBoards;

// *******************************************************************************
// HELPERS
// *******************************************************************************
// Applies a user's personal ordering (array of UIDs) over a canonical items array.
// Items not in customUids (newly added by admin) are appended at the end, sorted by
// canonical order. Falls back to canonical sort when customUids is empty/undefined.
function applyCustomOrder(items, customUids) {
  if (!customUids?.length) {
    return [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  const map = new Map(items.map(i => [i.uid || i._id, i]));
  const ordered = customUids.filter(uid => map.has(uid)).map(uid => map.get(uid));
  const customSet = new Set(customUids);
  const newItems = [...items]
    .filter(i => !customSet.has(i.uid || i._id))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  return [...ordered, ...newItems];
}
